/**
 * GridCrash — Haptics
 *
 * Centralized haptic feedback wrapper around @capacitor/haptics.
 * Fails gracefully everywhere:
 *   - Native iOS (Capacitor)  → Taptic Engine impacts/notifications
 *   - Mobile web browser      → navigator.vibrate fallback
 *   - Desktop browser         → silent no-op
 *
 * All functions are fire-and-forget; callers never await them.
 */

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

const isNative = Capacitor.isNativePlatform();

/** navigator.vibrate fallback for mobile web; silent if unsupported. */
function vibrate(pattern: number[]): void {
  try { navigator.vibrate?.(pattern); } catch { /* ignore */ }
}

/** Light impact — standard cell reveals. */
export function hapticLight(): void {
  if (isNative) {
    void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  } else {
    vibrate([10]);
  }
}

/** Medium impact — flag placement / secondary actions. */
export function hapticMedium(): void {
  if (isNative) {
    void Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
  } else {
    vibrate([20]);
  }
}

/** Heavy impact — bomb detonations. */
export function hapticHeavy(): void {
  if (isNative) {
    void Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {});
  } else {
    vibrate([10, 30, 10]);
  }
}

/** Success notification — full grid clear (fires with the win animation). */
export function hapticSuccess(): void {
  if (isNative) {
    void Haptics.notification({ type: NotificationType.Success }).catch(() => {});
  } else {
    vibrate([15, 60, 15, 60, 30]);
  }
}
